import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Agent } from './api';
import { useSoftphone } from '@/store/softphone';
import { useConnection } from '@/store/connection';

/**
 * Kümülatif login (Vardiya) + kuyruk süreleri — eski 3cxAPP davranışı:
 * logout/mola sonrası tekrar login'de kaldığı yerden devam (vardiya penceresi içinde).
 *
 * Backend mutlak anchor'lar döner (shiftLoginAt, queueAccumulatedMs, queueOpenSince);
 * burada 1 sn'de bir lokal tik ile canlı sayar, 30 sn'de bir anchor'ları tazeler.
 *
 * Bağlantı koptuğunda (backend/PBX down, network offline) sayaçlar DONDURULUR —
 * yoksa stale anchor + 1sn tik yüzünden "Bağlı/Kuyrukta/Boşta" backend kapalıyken
 * bile tıklamaya devam ediyor (temsilci bağlıyım/kuyruktayım sanıyor). useStateTimer
 * ile aynı pattern: kopunca o anki değerde dondur, bağlantı gelince devam.
 */
export function useAgentDurations() {
  const qc = useQueryClient();
  const queueIn = useSoftphone((s) => s.queueIn);
  const breakMode = useSoftphone((s) => s.breakMode);
  const sipState = useSoftphone((s) => s.state);
  const lastCallEndedAtLocal = useSoftphone((s) => s.lastCallEndedAt);
  // Tek liveness otoritesi: backend WS heartbeat (connection store). Backend
  // ulaşılamıyorsa ('offline') süreler donar — veri güvenilmez. 'connecting'
  // (ilk bağlanma) DONDURMAZ → başlangıç flaşı yok. WS pong-timeout REST-probe'dan
  // çok daha hızlı offline'a düşürür.
  const connStatus = useConnection((s) => s.status);
  const serverTimeOffset = useConnection((s) => s.serverTimeOffset);
  const healthy = connStatus !== 'offline'; // backend ulaşılabilir mi (REST poll gate)
  // Telefon (SIP) hazır mı? Topbar "Bağlanıyor"/"Bağlantı yok" gösterirken sayaçlar
  // TIKLAMASIN — topbar ile tutarlı olsun (sayaçlar yalnız SIP registered/çağrıdayken işler).
  const onCall = sipState === 'in_call' || sipState === 'ringing' || sipState === 'calling';
  const sipReady = sipState === 'registered' || onCall;
  // Sayaçların canlı tiklediği durum: backend ulaşılabilir + telefon hazır. Değilse donar.
  const live = healthy && sipReady;

  const { data } = useQuery({
    queryKey: ['agent-durations'],
    queryFn: Agent.durations,
    // WS heartbeat (15s) artık bu cache'i besliyor (Faz 2 sunucu-otoriteli push,
    // AppShell ws.on('durations')). REST sadece ilk-yükleme + WS push'u kaçarsa
    // fallback → 60s yeterli. queueIn/breakMode değişiminde aşağıdaki invalidate
    // anında tazeler.
    refetchInterval: 60_000,
    staleTime: 15_000,
    enabled: healthy, // backend offline'ken poll'u durdur (zaten donduruluyor)
  });

  // Kuyruk/mola durumu değişince anchor'ları HEMEN tazele — yoksa logout sonrası
  // backend'in stale queueOpenSince'i yüzünden "Kuyrukta Toplam" sayacı tiklemeye
  // devam ediyordu (sadece 30s poll ya da sayfa değişiminde duruyordu). İki kez
  // invalidate: anında + 1.5s sonra (activity_logs yazımı yerleşsin diye).
  useEffect(() => {
    qc.invalidateQueries({ queryKey: ['agent-durations'] });
    const id = setTimeout(() => qc.invalidateQueries({ queryKey: ['agent-durations'] }), 1500);
    return () => clearTimeout(id);
  }, [queueIn, breakMode, qc]);

  // Backend down / SIP hazır değilken "now"u dondur — sayaçlar o anki değerde kalsın
  // (düşmesin), durum düzelince kaldığı yerden devam. Sunucu-saatiyle (anchor'lar
  // sunucu zaman damgası → saat kayması bozmasın).
  const [frozenNow, setFrozenNow] = useState<number | null>(null);
  useEffect(() => {
    if (!live && frozenNow === null) setFrozenNow(Date.now() + serverTimeOffset);
    else if (live && frozenNow !== null) setFrozenNow(null);
  }, [live, frozenNow, serverTimeOffset]);

  // Canlı tik — sadece live iken; donmuşken render gereksiz.
  const [, force] = useState(0);
  useEffect(() => {
    if (!live) return;
    const id = setInterval(() => force((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [live]);

  if (!data) {
    return {
      onlineSec: 0, queueSec: 0, molaSec: 0, idleSec: 0,
      online: false, inQueue: false, onBreak: false, onCall, stale: !healthy, shiftHours: 12,
    };
  }

  // Donmuşsa o andaki zaman, değilse sunucu-senkron şimdi — açık segment delta'sı buna göre.
  const now = frozenNow ?? (Date.now() + serverTimeOffset);
  const onlineOpenMs = data.onlineOpenSince
    ? Math.max(0, now - new Date(data.onlineOpenSince).getTime())
    : 0;
  // ── Açık queue/break segmentini YETKİLİ anlık state ile GATE'le ─────────────
  // Backend anchor (activity_logs) stale/3CX ile çelişik olabilir (kapanmamış
  // queue_login, deploy/refresh/outage sonrası). Canlı tik SADECE store'daki
  // gerçek duruma (queueIn/breakMode — 3CX'ten sync) uyunca işler. Kuyruk-dışıyken
  // (queueIn=false) Kuyrukta TIKLAMAZ, accumulated'da donar. (Bug: toggle kapalıyken
  // sayaç çalışıyordu / outage sonrası yanlış resume.)
  const queueOpenMs = (data.queueOpenSince && queueIn && !breakMode)
    ? Math.max(0, now - new Date(data.queueOpenSince).getTime())
    : 0;
  const breakOpenMs = (data.breakOpenSince && breakMode)
    ? Math.max(0, now - new Date(data.breakOpenSince).getTime())
    : 0;

  // ── Boşta (idle): kuyruktayken (gerçek), çağrıda DEĞİLken, son çağrı bitişinden
  //    (ya da kuyruğa girişten) beri geçen süre. ──
  const inQueue = !!data.queueOpenSince && queueIn && !breakMode;
  const queueOpenTs = data.queueOpenSince ? new Date(data.queueOpenSince).getTime() : 0;
  const backendLastCallEnd = data.lastCallEndedAt ? new Date(data.lastCallEndedAt).getTime() : 0;
  const idleAnchor = Math.max(queueOpenTs, backendLastCallEnd, lastCallEndedAtLocal ?? 0);
  const idleSec = inQueue && !onCall && idleAnchor > 0
    ? Math.max(0, Math.floor((now - idleAnchor) / 1000))
    : 0;

  return {
    onlineSec: Math.floor((data.onlineAccumulatedMs + onlineOpenMs) / 1000), // Bağlı (online)
    queueSec: Math.floor((data.queueAccumulatedMs + queueOpenMs) / 1000),    // Kuyrukta = hazır (mola HARİÇ, mola=kuyruktan çıkış)
    molaSec: Math.floor((data.breakAccumulatedMs + breakOpenMs) / 1000),     // Mola toplam
    idleSec,                                                                 // Boşta (çağrı beklerken geçen anlık süre)
    // Nokta/pulse SADECE live iken aktif — SIP "Bağlanıyor" ya da backend down'da
    // sayaç donuk + nokta gri (topbar ile tutarlı).
    online: !!data.onlineOpenSince && live,
    inQueue: inQueue && live,
    onBreak: !!data.breakOpenSince && breakMode && live,
    onCall,
    stale: !healthy, // backend down → "bağlantı yok" ipucu (SIP-registering'de değil)
    shiftHours: data.shiftHours,
  };
}
